import { supabase } from '../lib/supabase';

export const notificationService = {
  // Get notification logs
  async getNotificationLogs(filters = {}) {
    try {
      let query = supabase?.from('notification_logs')?.select('*')?.order('created_at', { ascending: false })?.limit(filters?.limit || 100);

      if (filters?.notificationType) {
        query = query?.eq('notification_type', filters?.notificationType);
      }

      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }

      if (filters?.recipientEmail) {
        query = query?.eq('recipient_email', filters?.recipientEmail);
      }

      const { data, error } = await query;

      if (error) throw error;

      return data?.map(log => ({
        id: log?.id,
        notificationType: log?.notification_type,
        recipientEmail: log?.recipient_email,
        recipientName: log?.recipient_name,
        subject: log?.subject,
        message: log?.message,
        status: log?.status,
        sentAt: log?.sent_at,
        errorMessage: log?.error_message,
        referenceType: log?.reference_type,
        referenceId: log?.reference_id,
        metadata: log?.metadata,
        createdAt: log?.created_at,
      })) || [];
    } catch (error) {
      console.error('Error fetching notification logs:', error);
      throw error;
    }
  },

  // Get notification statistics
  async getNotificationStats(days = 7) {
    try {
      const startDate = new Date();
      startDate?.setDate(startDate?.getDate() - days);

      const { data, error } = await supabase?.from('notification_logs')?.select('notification_type, status')?.gte('created_at', startDate?.toISOString());

      if (error) throw error;

      // Calculate statistics
      const stats = {
        total: data?.length || 0,
        sent: data?.filter(n => n?.status === 'sent')?.length || 0,
        failed: data?.filter(n => n?.status === 'failed')?.length || 0,
        pending: data?.filter(n => n?.status === 'pending')?.length || 0,
        byType: {},
      };

      // Group by type
      data?.forEach(notification => {
        const type = notification?.notification_type;
        if (!stats?.byType?.[type]) {
          stats.byType[type] = { total: 0, sent: 0, failed: 0 };
        }
        stats.byType[type].total++;
        if (notification?.status === 'sent') stats.byType[type].sent++;
        if (notification?.status === 'failed') stats.byType[type].failed++;
      });

      return stats;
    } catch (error) {
      console.error('Error fetching notification stats:', error);
      throw error;
    }
  },

  // Get user notification preferences
  async getNotificationPreferences(userId) {
    // notification_preferences table does not exist in schema
    // Return default preferences
    return {
      userId: userId,
      emailEnabled: true,
      inventoryAlerts: true,
      scheduleAssignments: true,
      scheduleReminders: true,
      urgentAlerts: true,
      quietHoursStart: null,
      quietHoursEnd: null,
      digestFrequency: 'immediate',
    };
  },

  // Update notification preferences
  async updateNotificationPreferences(userId, preferences) {
    // notification_preferences table does not exist in schema
    // Return the preferences as-is (no persistence)
    console.warn('notification_preferences table does not exist. Preferences not saved.');
    return {
      user_id: userId,
      ...preferences,
    };
  },

  // Send test notification
  async sendTestNotification(recipientEmail, recipientName) {
    try {
      const { data, error } = await supabase?.functions?.invoke('send-email', {
        body: {
          emailType: 'general',
          to: recipientEmail,
          data: {
            recipientName: recipientName,
            subject: 'Test de Notification - ORAZEK',
            message: 'Ceci est un email de test pour vérifier que votre système de notifications fonctionne correctement.',
          },
        },
      });

      if (error) throw error;

      // Log notification
      await supabase?.from('notification_logs')?.insert([{
          notification_type: 'general',
          recipient_email: recipientEmail,
          recipient_name: recipientName,
          subject: 'Test de Notification - ORAZEK',
          message: 'Email de test',
          status: 'sent',
          sent_at: new Date()?.toISOString(),
        }]);

      return data;
    } catch (error) {
      console.error('Error sending test notification:', error);
      throw error;
    }
  },

  // Real-time subscription for notification logs
  subscribeToNotificationLogs(callback) {
    const channel = supabase
      ?.channel('notification_logs_changes')
      ?.on('postgres_changes', { event: '*', schema: 'public', table: 'notification_logs' }, (payload) => {
        callback(payload);
      })
      ?.subscribe();

    return () => supabase?.removeChannel(channel);
  },

  // Real-time subscription for quotes (quote requests)
  subscribeToQuotes(callback) {
    const channel = supabase
      ?.channel('quotes_changes')
      ?.on('postgres_changes', { event: '*', schema: 'public', table: 'quotes' }, (payload) => {
        callback(payload);
      })
      ?.subscribe();

    return () => supabase?.removeChannel(channel);
  },

  // Real-time subscription for project milestones
  subscribeToProjectMilestones(callback) {
    const channel = supabase
      ?.channel('project_milestones_changes')
      ?.on('postgres_changes', { event: '*', schema: 'public', table: 'project_milestones' }, (payload) => {
        callback(payload);
      })
      ?.subscribe();

    return () => supabase?.removeChannel(channel);
  },

  // Real-time subscription for team schedules (team assignments)
  subscribeToTeamSchedules(callback) {
    const channel = supabase
      ?.channel('team_schedules_changes')
      ?.on('postgres_changes', { event: '*', schema: 'public', table: 'team_schedules' }, (payload) => {
        callback(payload);
      })
      ?.subscribe();

    return () => supabase?.removeChannel(channel);
  },

  // Real-time subscription for orders (payment confirmations)
  subscribeToOrders(callback) {
    const channel = supabase
      ?.channel('orders_changes')
      ?.on('postgres_changes', { event: '*', schema: 'public', table: 'orders' }, (payload) => {
        callback(payload);
      })
      ?.subscribe();

    return () => supabase?.removeChannel(channel);
  },

  // Mark notification as read
  async markAsRead(notificationId) {
    try {
      const { data, error } = await supabase
        ?.from('notification_logs')
        ?.update({ status: 'read', read_at: new Date()?.toISOString() })
        ?.eq('id', notificationId)
        ?.select();

      if (error) throw error;

      return data?.[0];
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  },

  // Batch mark as read
  async batchMarkAsRead(notificationIds) {
    try {
      const { data, error } = await supabase
        ?.from('notification_logs')
        ?.update({ status: 'read', read_at: new Date()?.toISOString() })
        ?.in('id', notificationIds)
        ?.select();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Error batch marking notifications as read:', error);
      throw error;
    }
  },
};